import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, errText, assetUrl } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import DenzelShareButton from "../components/DenzelShareButton";
import { Share2, RefreshCw, Undo2, ArrowLeft, Instagram, Facebook, Zap, X, ChevronRight, ExternalLink, Copy } from "lucide-react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const PLATFORM_ICON = { facebook: Facebook, instagram: Instagram };
const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

export default function DenzelShareTray() {
  const [tab, setTab] = useState("pending");
  const [pending, setPending] = useState([]);
  const [shared, setShared] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, shared: 0 });
  const [loading, setLoading] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [bulkIdx, setBulkIdx] = useState(-1);   // -1 = closed, >=0 = active step
  const [bulkList, setBulkList] = useState([]);
  const [bulkShared, setBulkShared] = useState(0);
  const [awaitingReturn, setAwaitingReturn] = useState(false);  // FB popup opened, waiting for tab focus back

  const loadTab = async (which) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/denzel/share-tray?status=${which}`);
      if (which === "pending") setPending(data.posts || []);
      else setShared(data.posts || []);
      setCounts(data.counts || { pending: 0, shared: 0 });
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTab(tab); }, [tab]);

  const runBackfill = async () => {
    setBackfilling(true);
    const w = toast.loading("Fetching FB/IG permalinks for existing published posts…");
    try {
      const { data } = await api.post("/denzel/share-tray/backfill-permalinks");
      toast.dismiss(w);
      toast.success(`Backfill done: ${data.updated} added, ${data.failed} failed.`);
      loadTab(tab);
    } catch (e) {
      toast.dismiss(w);
      toast.error(errText(e));
    } finally {
      setBackfilling(false);
    }
  };

  const markPending = async (post_id) => {
    try {
      await api.post(`/denzel/share-tray/${post_id}/mark-pending`);
      toast.success("Moved back to pending");
      loadTab(tab);
    } catch (e) { toast.error(errText(e)); }
  };

  // ---- Bulk share wizard ----
  const startBulk = () => {
    const list = pending.filter((p) => p.permalink_url);
    if (list.length === 0) { toast.message("Nothing to bulk-share"); return; }
    setBulkList(list);
    setBulkIdx(0);
    setBulkShared(0);
  };
  const closeBulk = () => { setBulkIdx(-1); setBulkList([]); loadTab(tab); };
  const bulkCurrent = bulkIdx >= 0 ? bulkList[bulkIdx] : null;
  const bulkAdvance = () => {
    if (bulkIdx + 1 >= bulkList.length) { closeBulk(); toast.success(`Bulk share complete · ${bulkShared} shared`); }
    else setBulkIdx(bulkIdx + 1);
  };
  const bulkShareCurrent = async () => {
    if (!bulkCurrent) return;
    // 1. Copy follow snippet
    if (bulkCurrent.follow_snippet) {
      try { await navigator.clipboard.writeText(bulkCurrent.follow_snippet); } catch (_e) {}
    }
    // 2. Open FB sharer popup (do NOT mark shared yet — wait for user to return)
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(bulkCurrent.permalink_url)}`;
    window.open(url, "_blank", "noopener,noreferrer,width=680,height=700");
    setAwaitingReturn(true);
  };
  const bulkConfirmShared = async () => {
    if (!bulkCurrent) return;
    const postForUndo = bulkCurrent;
    try {
      await api.post(`/denzel/share-tray/${bulkCurrent.id}/mark-shared`);
      setBulkShared((n) => n + 1);
      setAwaitingReturn(false);
      toast.success(`Shared: ${postForUndo.title}`, {
        duration: 5000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post(`/denzel/share-tray/${postForUndo.id}/mark-pending`);
              setBulkShared((n) => Math.max(0, n - 1));
              toast.message("Reverted — moved back to pending");
              loadTab(tab);
            } catch (e) { toast.error(errText(e)); }
          },
        },
      });
      bulkAdvance();
    } catch (e) { toast.error(errText(e)); }
  };
  const bulkNotYet = () => {
    setAwaitingReturn(false);
  };
  const bulkSkip = () => { setAwaitingReturn(false); bulkAdvance(); };
  const bulkCopySnippet = async () => {
    if (!bulkCurrent?.follow_snippet) return;
    try { await navigator.clipboard.writeText(bulkCurrent.follow_snippet); toast.success("Snippet copied"); }
    catch (_e) { toast.error("Copy failed"); }
  };

  // Auto-return detection: when the user comes back to this tab after clicking Share,
  // pop a "Did you post it?" confirmation instead of blindly marking shared.
  useEffect(() => {
    if (!awaitingReturn) return;
    const onFocus = () => { /* focus alone isn't enough — visibilitychange is more reliable */ };
    const onVis = () => {
      if (document.visibilityState === "visible" && awaitingReturn) {
        // Small delay so the click that returned focus isn't swallowed by dialog rerender
        setTimeout(() => {
          toast.info("Welcome back — did you post it to Facebook?");
        }, 200);
      }
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [awaitingReturn]);

  const rows = tab === "pending" ? pending : shared;

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6" data-testid="denzel-share-page">
      <div>
        <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-2">
          <ArrowLeft size={12} /> Back to Admin
        </Link>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Umbrella cross-post</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold flex items-center gap-2">
              <Share2 size={22} /> Denzel Share Tray
            </h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Facebook doesn't allow apps to post to personal profiles, so every native FB/IG publish is queued here.
              One click opens FB's share dialog with the full post preview loaded, and copies a
              "follow {"{brand}"}" snippet you can paste on top.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {tab === "pending" && pending.length > 0 && (
              <Button size="sm" onClick={startBulk} data-testid="denzel-bulk-share-btn">
                <Zap size={13} className="mr-2" />
                Share all pending ({pending.length})
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={runBackfill} disabled={backfilling}
                    data-testid="denzel-backfill-btn">
              <RefreshCw size={13} className={`mr-2 ${backfilling ? "animate-spin" : ""}`} />
              {backfilling ? "Fetching…" : "Fetch missing permalinks"}
            </Button>
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="pending" data-testid="denzel-tab-pending">
            Pending {counts.pending ? <Badge variant="secondary" className="ml-2">{counts.pending}</Badge> : null}
          </TabsTrigger>
          <TabsTrigger value="shared" data-testid="denzel-tab-shared">
            Shared {counts.shared ? <Badge variant="secondary" className="ml-2">{counts.shared}</Badge> : null}
          </TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="pt-4">
          {loading && rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-sm text-muted-foreground" data-testid="denzel-empty-state">
                {tab === "pending"
                  ? "No pending posts to share right now — as soon as a brand post publishes to Facebook or Instagram, it'll appear here."
                  : "Nothing shared yet."}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {rows.map((p) => <SharePostCard key={p.id} post={p} onChanged={() => loadTab(tab)}
                                              onMarkPending={() => markPending(p.id)} />)}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={bulkIdx >= 0} onOpenChange={(o) => { if (!o) closeBulk(); }}>
        <DialogContent className="sm:max-w-lg" data-testid="denzel-bulk-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Zap size={18} /> Bulk share to Denzel
            </DialogTitle>
          </DialogHeader>
          {bulkCurrent && (
            <div className="space-y-4">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span data-testid="denzel-bulk-progress">Post {bulkIdx + 1} of {bulkList.length}</span>
                <span className="text-accent">{bulkShared} shared so far</span>
              </div>
              <div className="rounded-lg overflow-hidden border bg-muted">
                <div className="aspect-[9/16] max-h-80 relative flex items-center justify-center bg-black/5">
                  {bulkCurrent.video_poster_url ? (
                    <img src={assetUrl(bulkCurrent.video_poster_url)} className="w-full h-full object-cover" alt="" />
                  ) : bulkCurrent.image_url ? (
                    <img src={assetUrl(bulkCurrent.image_url)} className="w-full h-full object-cover" alt="" />
                  ) : (
                    <div className="text-xs text-muted-foreground">No media</div>
                  )}
                  <Badge className="absolute top-2 left-2 border-0 text-[10px] px-1.5 py-0.5 bg-black/70 text-white capitalize">
                    {bulkCurrent.platform}
                  </Badge>
                  {bulkCurrent.brand && (
                    <Badge className="absolute top-2 right-2 border-0 text-[10px] px-1.5 py-0.5 bg-white/90 text-black">
                      {bulkCurrent.brand}
                    </Badge>
                  )}
                </div>
              </div>
              <div>
                <div className="font-heading text-base">{bulkCurrent.title}</div>
                <p className="text-xs text-muted-foreground line-clamp-3 mt-1 whitespace-pre-line">{bulkCurrent.copy}</p>
              </div>
              {bulkCurrent.follow_snippet && (
                <div className="rounded-md border border-dashed p-2 bg-muted/40 text-[11px]" data-testid="denzel-bulk-snippet">
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-semibold text-[10px] uppercase tracking-wide text-muted-foreground">Paste this into FB's "Say something…"</div>
                    <button onClick={bulkCopySnippet} className="text-muted-foreground hover:text-foreground" title="Copy snippet"><Copy size={11} /></button>
                  </div>
                  <div className="whitespace-pre-line">{bulkCurrent.follow_snippet}</div>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2">
                {bulkCurrent.follow_urls?.facebook && (
                  <a href={bulkCurrent.follow_urls.facebook} target="_blank" rel="noopener noreferrer"
                     className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                    <ExternalLink size={11} /> Follow on FB
                  </a>
                )}
                {bulkCurrent.follow_urls?.instagram && (
                  <a href={bulkCurrent.follow_urls.instagram} target="_blank" rel="noopener noreferrer"
                     className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                    <ExternalLink size={11} /> Follow on IG
                  </a>
                )}
              </div>
              <div className="flex items-center gap-2 pt-2 border-t">
                {awaitingReturn ? (
                  <>
                    <div className="flex-1 text-xs" data-testid="denzel-bulk-return-prompt">
                      <div className="font-semibold text-accent">Did you post it to Facebook?</div>
                      <div className="text-muted-foreground mt-0.5">Confirm so we mark it shared, or say Not yet to keep it pending.</div>
                    </div>
                    <Button onClick={bulkConfirmShared} data-testid="denzel-bulk-confirm-btn">
                      <Share2 size={13} className="mr-2" /> Yes, mark shared
                    </Button>
                    <Button variant="outline" onClick={bulkNotYet} data-testid="denzel-bulk-notyet-btn">
                      Not yet
                    </Button>
                  </>
                ) : (
                  <>
                    <Button onClick={bulkShareCurrent} data-testid="denzel-bulk-share-current-btn" className="flex-1">
                      <Share2 size={13} className="mr-2" /> Open FB (we'll confirm on return)
                    </Button>
                    <Button variant="outline" onClick={bulkSkip} data-testid="denzel-bulk-skip-btn">
                      Skip <ChevronRight size={13} className="ml-1" />
                    </Button>
                    <Button variant="ghost" onClick={closeBulk} data-testid="denzel-bulk-close-btn">
                      <X size={14} />
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SharePostCard({ post, onChanged, onMarkPending }) {
  const Icon = PLATFORM_ICON[post.platform] || Share2;
  const isShared = post.denzel_share_status === "shared";
  const [previewCopy, setPreviewCopy] = useState(false);
  const copy = post.copy || "";
  const trimmedCopy = copy.length > 220 ? copy.slice(0, 220) + "…" : copy;
  const followUrls = post.follow_urls || {};
  return (
    <Card className="flex flex-col overflow-hidden" data-testid={`denzel-row-${post.id}`}>
      <div className="relative bg-muted aspect-[9/16] w-full overflow-hidden">
        {post.video_poster_url ? (
          <img src={assetUrl(post.video_poster_url)} alt="" className="w-full h-full object-cover" />
        ) : post.image_url ? (
          <img src={assetUrl(post.image_url)} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/50 text-xs">no media</div>
        )}
        <Badge className="absolute top-2 left-2 border-0 text-[10px] px-1.5 py-0.5 bg-black/70 text-white capitalize">
          <Icon size={10} className="mr-1" />{post.platform}
        </Badge>
        {post.brand && (
          <Badge className="absolute top-2 right-2 border-0 text-[10px] px-1.5 py-0.5 bg-white/90 text-black">
            {post.brand}
          </Badge>
        )}
        {isShared && (
          <div className="absolute inset-0 flex items-end justify-end p-2 pointer-events-none">
            <Badge className="bg-green-600 text-white border-0">✓ Shared</Badge>
          </div>
        )}
      </div>
      <CardHeader className="pb-2">
        <CardTitle className="font-heading text-base line-clamp-2">{post.title}</CardTitle>
        <p className="text-[11px] text-muted-foreground">Published {fmtDate(post.published_at)}</p>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col gap-3 pt-0">
        <button className="text-xs text-muted-foreground text-left whitespace-pre-line line-clamp-3 hover:text-foreground"
                onClick={() => setPreviewCopy((v) => !v)} title="Toggle full caption">
          {previewCopy ? copy : trimmedCopy}
        </button>
        {post.follow_snippet && (
          <div className="rounded-md border border-dashed p-2 bg-muted/40 text-[11px]" data-testid={`denzel-follow-snippet-${post.id}`}>
            <div className="font-semibold text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Paste this into FB's "Say something…"</div>
            <div className="whitespace-pre-line">{post.follow_snippet}</div>
          </div>
        )}
        <div className="mt-auto space-y-2">
          <DenzelShareButton post={post} size="sm" variant="default" onShared={onChanged}
                             className="justify-start" />
          {isShared && (
            <Button size="sm" variant="ghost" onClick={onMarkPending}
                    data-testid={`denzel-mark-pending-${post.id}`} className="text-xs">
              <Undo2 size={12} className="mr-1" /> Move back to pending
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
